import React, { memo, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import type { Slide, CarouselKind, CellContentContainer } from '@segmently/schema-models/v2';
import type { PreviewMode } from '../../../types/previewMode';
import type { TemplateVariables } from '../../../utils/contentResolver';
import { mapEdgeInsetsToCSS } from '../../../utils/styleMapper';
import { UnpaintedContentScope } from '../../../animations';
import {
  carouselShowsDetail,
  carouselShowsSubtitle,
  carouselShowsTitle,
  imageRenders,
} from '../../../utils/renderPredicates';
import { Text } from '../Text';
import { Image } from '../Image';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import styles from './SliderItem.module.css';

export interface SliderItemProps {
  slide: Slide | undefined;
  locale: string;
  isActive?: boolean;
  carouselKind?: CarouselKind;
  imageContainer?: CellContentContainer;
  textContainer?: CellContentContainer;
  previewMode?: PreviewMode;
  variables?: TemplateVariables;
  resolvedScreenValues?: Record<string, string>;
  editPath?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Maps CellContentContainer to CSS properties for absolutely-positioned layers.
 *
 * Containers are independent layers within the slide cell (not flex children).
 * - heightKind=percent → height as percentage of cell
 * - heightKind=fixed → height in px
 * - heightKind=auto → natural content height
 * - verticalAlignment → anchors the container within the cell (top/center/bottom)
 *   AND sets justifyContent for content alignment within the container
 * - insets → padding
 */
function mapContainerStyle(container?: CellContentContainer, defaultPercent = 50): CSSProperties {
  const style: CSSProperties = {};

  // Height
  const heightKind = container?.heightKind;
  const heightValue = container?.heightValue;

  if (heightKind === 'percent' && typeof heightValue === 'number') {
    style.height = `${heightValue}%`;
  } else if (heightKind === 'fixed' && typeof heightValue === 'number') {
    style.height = `${heightValue}px`;
  } else if (heightKind === 'auto') {
    style.height = 'auto';
  } else {
    style.height = `${defaultPercent}%`;
  }

  // Vertical alignment = anchor position within the cell
  const alignment = container?.verticalAlignment;
  if (alignment === 'center') {
    style.top = '50%';
    style.transform = 'translateY(-50%)';
  } else if (alignment === 'bottom') {
    style.top = 'auto';
    style.bottom = 0;
  } else {
    // 'top' or default
    style.top = 0;
  }

  // Content alignment: CSS class defaults handle it (textContainer = flex-start).
  // verticalAlignment controls anchor position only, NOT content alignment.

  // Insets → padding
  if (container?.insets) {
    Object.assign(style, mapEdgeInsetsToCSS(container.insets));
  }

  return style;
}

function parsePercent(value: CSSProperties[keyof CSSProperties]): number | null {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return null;
  if (!value.endsWith('%')) return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function resolveMissingImageTextContainerStyle(
  imageStyle: CSSProperties,
  textStyle: CSSProperties,
): CSSProperties {
  const imageTop = parsePercent(imageStyle.top) ?? 0;
  const imageHeight = parsePercent(imageStyle.height);
  const textTop = parsePercent(textStyle.top) ?? 0;

  if (
    imageHeight == null ||
    textStyle.transform ||
    imageStyle.transform ||
    imageStyle.bottom != null ||
    textStyle.bottom != null
  ) {
    return textStyle;
  }

  const imageBottom = imageTop + imageHeight;
  const textStartsInsideImage = textTop < imageBottom;
  if (!textStartsInsideImage) return textStyle;

  return {
    ...textStyle,
    top: `${Math.min(100, imageBottom)}%`,
  };
}

// Rows by kind — shared with the entrance plan (utils/renderPredicates.ts), which staggers only the
// rows a slide renders (D-F14d)
const shouldShowTitle = carouselShowsTitle;
const shouldShowSubtitle = carouselShowsSubtitle;
const shouldShowSubtitle2 = carouselShowsDetail;

export const SliderItem = memo(function SliderItem({
  slide,
  locale,
  isActive = false,
  carouselKind,
  imageContainer,
  textContainer,
  previewMode,
  variables,
  resolvedScreenValues,
  editPath,
  className,
  'data-testid': testId,
}: SliderItemProps): React.ReactElement | null {
  // ⚠️ Rules of Hooks: hooks first, the null-slide/content early-returns after
  // — a slide's content can materialize mid-session (theme/editor writes) and
  // a pre-hook return flips the hook count ("Rendered more hooks…" crash).
  const content = slide?.content;

  // Per-slide entrance (F14 / AMV-AC-037): the image/text classes sit on every slide, and a hidden
  // slide is display:none, so each showing replays its entrance. On the screen the static page
  // painted, only the slide shown at mount was painted: it keeps the takeover skip (AMV-AC-036)
  // until it is hidden once. A slide hidden at mount, or hidden since, was never painted as it is
  // shown next — its entrance is not a replay. Derived state (StrictMode-idempotent).
  const [shownSinceMount, setShownSinceMount] = useState(isActive);
  if (shownSinceMount && !isActive) setShownSinceMount(false);

  const containerClassName = isActive
    ? (className ? `${styles.sliderItem} ${className}` : styles.sliderItem)
    : (className ? `${styles.sliderItem} ${styles.hidden} ${className}` : `${styles.sliderItem} ${styles.hidden}`);

  const hasTextContent = shouldShowTitle(carouselKind) || shouldShowSubtitle(carouselKind) || shouldShowSubtitle2(carouselKind);
  const hasResolvedImage = imageRenders(content?.image, locale);

  // Compute container styles from CellContentContainer props
  const imageContainerStyle = useMemo(() => mapContainerStyle(imageContainer, 50), [imageContainer]);
  const textContainerStyle = useMemo(() => mapContainerStyle(textContainer, 50), [textContainer]);
  const effectiveTextContainerStyle = useMemo(() => {
    if (hasResolvedImage) return textContainerStyle;
    return resolveMissingImageTextContainerStyle(imageContainerStyle, textContainerStyle);
  }, [hasResolvedImage, imageContainerStyle, textContainerStyle]);

  const schemaTestId = testId ?? getSchemaTestId(editPath);

  if (!slide || !content) {
    return null;
  }

  const slideElement = (
    <div className={containerClassName} data-testid={schemaTestId} data-edit-path={editPath}>
      <div className={styles.imageContainer} style={imageContainerStyle}>
        {hasResolvedImage ? (
          <Image
            image={content.image}
            locale={locale}
            alt="Slider image"
            fillContainer
            previewMode={previewMode}
            editPath={editPath ? `${editPath}.content.image` : undefined}
          />
        ) : (
          <div
            className={styles.placeholder}
            data-testid={schemaTestId ? `${schemaTestId}.image-placeholder` : undefined}
            aria-hidden="true"
          />
        )}
      </div>

      {hasTextContent && (
        <div className={styles.textContainer} style={effectiveTextContainerStyle}>
          {shouldShowTitle(carouselKind) && (
            <Text
              label={content.title}
              locale={locale}
              variables={variables}
              resolvedScreenValues={resolvedScreenValues}
              editPath={editPath ? `${editPath}.content.title` : undefined}
            />
          )}
          {shouldShowSubtitle(carouselKind) && (
            <Text
              label={content.subtitle}
              locale={locale}
              variables={variables}
              resolvedScreenValues={resolvedScreenValues}
              editPath={editPath ? `${editPath}.content.subtitle` : undefined}
            />
          )}
          {shouldShowSubtitle2(carouselKind) && (content as any).detail && (
            <Text
              label={(content as any).detail}
              locale={locale}
              variables={variables}
              resolvedScreenValues={resolvedScreenValues}
              editPath={editPath ? `${editPath}.content.detail` : undefined}
            />
          )}
        </div>
      )}
    </div>
  );

  return <UnpaintedContentScope unpainted={!shownSinceMount}>{slideElement}</UnpaintedContentScope>;
});

SliderItem.displayName = 'SliderItem';
